import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {DoppeHideableValue} from '../../client-server-common/types/doppe-hideable-value';
import {
    doppeHideableValueCreateHidden,
    doppeHideableValueIsVisibleAndNotEqualValue
} from '../../client-server-common/types/doppe-hideable-value';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import type {PartiallyOptional, PartiallyRequired} from '@wix/devzai-utils-common';
import {arrayFlatten, arrayRemoveNullValues, asArray, immerProduce} from '@wix/devzai-utils-common';
import type {DoppeListActionSettings} from '../../client-server-common';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    DoppeActionLayout,
    DoppeActionListItemClickBehaviour,
    doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails,
    doppeListActionGetDefaultListSettings,
    doppeListActionGetDefaultSearchListSettings,
    doppeListActionGetItemClickBehaviourDefaultListSettings,
    doppeListActionGetMainDetailsDefaultSettings,
    doppeListActionItemCtaButtonsResetSettingsForLock,
    doppeListActionListItemCalculateItemButtons,
    doppeListActionListItemGetDefaultListItemSettingsMigration,
    doppeListActionListItemWithCtaButtonsMigration,
    doppeListActionListItemWithSectionsMigration,
    doppeListActionSettingsFilterHiddenListItemsFromActionView,
    doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration,
    doppeListActionTemplateSectionsSettingsDefaultValuesMigration,
    doppeListActionWithMainDetailsGetMainImage,
    doppeListActionWithMainDetailsReplaceMainImage
} from '../../client-server-common';
import {doppeActionCtaButtonsResetSettingsForLock} from '../../client-server-common/types/doppe-action-cta-button';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import type {RichTextContent} from '@wix/devzai-utils-react';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {DoppeActionWidgetPopupMode} from '../../client-server-common/types/doppe-action-widget-popup-mode';


export namespace DoppeActionTypeMediaGallery {

    export interface MediaGalleryListItemSettings extends
        DoppeListActionSettings.ListItemWithSections,
        DoppeListActionSettings.ListItemWithCtaButtons,
        DoppeListActionSettings.ListItemBaseSettings {
        media: WixMediaResource;
        title: string;
        description: DoppeHideableValue<RichTextContent>;
        ribbon: DoppeHideableValue<string>;
    }

    export interface ActionSettings extends
        DoppeListActionSettings<DoppeActionTypeMediaGallery.MediaGalleryListItemSettings>,
        DoppeListActionSettings.WithItemClickBehaviour,
        DoppeListActionSettings.WithMainDetails,
        DoppeListActionSettings.WithSearchSettings,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings {
        previewShowDescription: boolean;
    }

}

export function doppeActionTypeMediaGalleryResolveListItemSettings(mediaItem: PartiallyRequired<DoppeActionTypeMediaGallery.MediaGalleryListItemSettings, 'id'>) {

    const {
        id,
        deleted,
        displayInSearch,
        enabled,
        customSections,
        customCtaButtons,
        ctaButtonsOverridesMap,
        sectionsOverridesMap,
        // ctaButton,
        ...restProduct
    } = mediaItem;

    return {
        // ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
        //     hasSourceURL: false,
        //     ctaButton: ctaButton,
        // }),
        ...doppeListActionListItemGetDefaultListItemSettingsMigration(mediaItem.id, mediaItem),
        ...doppeListActionListItemWithCtaButtonsMigration(mediaItem, false),
        ...doppeListActionListItemWithSectionsMigration(mediaItem),
        ...restProduct as PartiallyOptional<DoppeActionTypeMediaGallery.MediaGalleryListItemSettings, 'enabled' | 'displayInSearch' | 'deleted' | 'id' | 'ctaButtonsOverridesMap' | 'sectionsOverridesMap' | 'customSections' | 'customCtaButtons'>
    }
}

export function doppeActionTypeMediaGalleryResolveActionSettings(actionSettings: Partial<DoppeActionTypeMediaGallery.ActionSettings>) {
    const {
        listDataItems = [],
        ctaButtons,
        sections,
        listSearchSettings,
        ...restSettings
    } = actionSettings;

    return {
        previewShowDescription: true,
        ...doppeListActionGetMainDetailsDefaultSettings(),
        ...doppeListActionGetItemClickBehaviourDefaultListSettings({
            itemClickBehaviour: DoppeActionListItemClickBehaviour.PreviewItemAsPopup,
        }),
        ...doppeListActionGetDefaultSearchListSettings(actionSettings),
        ...doppeListActionGetDefaultListSettings({
            layout: DoppeActionLayout.Carousel,
            listDataItems: listDataItems.map(doppeActionTypeMediaGalleryResolveListItemSettings),
        }),
        ...doppeActionContentSectionSettingsDefaultValuesMigration(actionSettings),
        ...doppeActionCtaButtonsSettingsDefaultValuesMigration(actionSettings, false),
        ...doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration(actionSettings, false),
        ...doppeListActionTemplateSectionsSettingsDefaultValuesMigration(actionSettings),
        ...restSettings
    }
}

export const doppeActionTypeMediaGallery = doppeActionTypeDefineMetadata<DoppeActionTypeMediaGallery.ActionSettings>({
    id: 'co.hopp.action/media-gallery',
    resolveActionSettings: action => {
        return doppeActionTypeMediaGalleryResolveActionSettings(action.settings);
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeListActionItemCtaButtonsResetSettingsForLock(actionSettings);
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            ...doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails()
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        }
    },
    actionListItemSettingsMetadata: {
        contentSettingsMetadata: {
            title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon)
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        }
    },
    isWidgetOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    isPaymentAction: false,
    isFormAction: false,
    isListItemLinkOpener: false,
    supportsListLayout: true,
    notSupportsListItemClickBeahviour: true,
    forceFullScreenOnListItemClick: true,
    resolveUsedMedia: actionSettings => {
        return arrayRemoveNullValues([...arrayFlatten(actionSettings.listDataItems.map(itemSettings => {
            return itemSettings.media ? asArray(itemSettings.media) : []
        })), doppeHideableValueIsVisibleAndNotEqualValue(actionSettings.mainImage, null) ? actionSettings.mainImage : null]);
    },
    resolveMainMedia: actionSettings => {
        return doppeListActionWithMainDetailsGetMainImage(actionSettings, actionSettings.listDataItems[0]?.media ?? null);
    },
    resolveMainLink: (actionSettings, htmlSiteInfo, viewerLang, bymoPageProps, viewerPagesRoutesManager) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            htmlSiteInfo: htmlSiteInfo,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager
        }) ?? null;
    },
    resolveCustomWidgetPopupMode: () => {
        return DoppeActionWidgetPopupMode.FullScreenModal;
    },
    resolveListItemsCount: actionSettings => {
        return doppeListActionSettingsFilterHiddenListItemsFromActionView(actionSettings.listDataItems).length;
    },
    resolveListItems: (action, htmlSiteInfo, viewerLang, bymoPageProps, viewerPagesRoutesManager) => {
        const actionSettings = action.settings;
        return actionSettings.listDataItems.map(item => {
            return {
                url: doppeActionCtaButtonsResolveMainURL(
                    doppeListActionListItemCalculateItemButtons(actionSettings.listItemTemplateCtaButtonsSettings, item), {
                        htmlSiteInfo: htmlSiteInfo,
                        viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager
                    }) ?? null,
                title: item.title,
                action: action,
                couponCode: doppeHideableValueCreateHidden(''),
                description: item.description,
                image: item.media,
                itemId: item.id,
                ribbon: item.ribbon,
                enabled: item.enabled,
                deleted: item.deleted,
                displayInSearch: item.displayInSearch
            }
        });
    },
    resolveListSearchSettings: actionSettings => {
        return actionSettings.listSearchSettings;
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            for (const item of actionSettings.listDataItems) {
                if (item.media) {
                    item.media = wixMediaResourceReplaceExternalImage(item.media, externalImagesUrlToImageResourceMap)
                }
            }

            doppeListActionWithMainDetailsReplaceMainImage(actionSettings, externalImagesUrlToImageResourceMap);
        })
    },
});
